import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Descriptions,
  message,
  Tabs,
  Alert,
  Tag,
  Space,
  Typography,
  Tooltip,
  Card,
  Divider,
  Row,
  Col,
  Badge,
  Button,
  Table,
  List,
  Avatar,
  Spin,
  Empty,
  Input,
} from "antd";
import {
  InfoCircleOutlined,
  EditOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  FileTextOutlined,
  UploadOutlined,
  CalendarOutlined,
  BookOutlined,
  RobotOutlined,
  PlusOutlined,
  TagsOutlined,
  SyncOutlined,
  BulbOutlined,
  LinkOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { KnowledgeItem, useGlobal } from "../../../contexts/GlobalContext";
import request from "../../../utils/request";
import {
  PodcastInfo,
  PodcastStatus,
  getSummarySwitchStatus,
  normalizePodcastConfig,
} from "../../../types/podcast";
import PodcastPlayer from "../Podcast/PodcastPlayer";

const { Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface HotWordItem {
  id: number;
  word: string;
  description: string;
  summary_id: number;
  created_at: string;
}

interface RecommendItem {
  id: number;
  title: string;
  summary: string;
  url: string;
  score: number;
  reason: string;
}

const getPodcastProgressText = (podcast: PodcastInfo | null) => {
  if (!podcast) {
    return "";
  }

  switch (podcast.status) {
    case PodcastStatus.Pending:
      return "播客任务已提交，等待生成中...";
    case PodcastStatus.Processing:
      return "播客正在生成中...";
    case PodcastStatus.Completed:
      return "";
    case PodcastStatus.Failed:
      return podcast.error_msg || "播客生成失败";
    default:
      return "";
  }
};

interface KnowledgeEditModalProps {
  visible: boolean;
  onCancel: () => void;
  currentRecord: KnowledgeItem | null;
  onSuccess: () => void;
  getProjectName: (id: number) => string;
}

const KnowledgeEditModal: React.FC<KnowledgeEditModalProps> = ({
  visible,
  onCancel,
  currentRecord,
  onSuccess,
  getProjectName,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("content");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { API_BASE_URL, token, projects } = useGlobal();
  const [hotWords, setHotWords] = useState<HotWordItem[]>([]);
  const [hotWordsLoading, setHotWordsLoading] = useState(false);
  const [generatingHotWords, setGeneratingHotWords] = useState(false);
  const [hotWordsTotal, setHotWordsTotal] = useState(0);
  // 添加重建索引状态
  const [rebuildingIndex, setRebuildingIndex] = useState(false);

  const [recommends, setRecommends] = useState<RecommendItem[]>([]);
  const [recommendsLoading, setRecommendsLoading] = useState(false);
  const [recommendsTotal, setRecommendsTotal] = useState(0);
  const [generatingRecommends, setGeneratingRecommends] = useState(false);

  // 播客相关状态
  const [podcast, setPodcast] = useState<PodcastInfo | null>(null);
  const [podcastLoading, setPodcastLoading] = useState(false);
  const [playerVisible, setPlayerVisible] = useState(false);
  const [currentPodcast, setCurrentPodcast] = useState<PodcastInfo | null>(null);
  const [generatingPodcast, setGeneratingPodcast] = useState(false);
  const [podcastWhitelistStatus, setPodcastWhitelistStatus] = useState<boolean | null>(null);
  const [podcastConfig, setPodcastConfig] = useState<any>(null);
  const [generatingProgress, setGeneratingProgress] = useState<string>("");
  const podcastPollingTimerRef = React.useRef<number | null>(null);

  const isHotWordsSupported = () => {
    if (!currentRecord) return false;
    const project = projects.find((p) => p.id === currentRecord.project_id);
    return project?.is_postchat || false;
  };

  // 检查AI推荐是否支持（仅PostChat项目支持）
  const isRecommendsSupported = () => {
    if (!currentRecord) return false;
    const project = projects.find((p) => p.id === currentRecord.project_id);
    return project?.is_postchat || false;
  };

  // 获取AI推荐列表
  const fetchRecommends = React.useCallback(
    async (summaryId: number) => {
      if (!summaryId) return;

      setRecommendsLoading(true);
      try {
        const response = await request.get<{
          code: number;
          msg: string;
          data: RecommendItem[];
        }>(`${API_BASE_URL}/api/v3/recommendations`, {
          params: {
            summary_id: summaryId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code === 200) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          setRecommends(list);
          setRecommendsTotal(list.length);
        } else {
          message.error(response.data.msg || "获取AI推荐列表失败");
        }
      } catch (error) {
        console.error("获取AI推荐列表失败:", error);
        message.error("获取AI推荐列表失败，请稍后重试");
      } finally {
        setRecommendsLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  const handleGenerateRecommends = async () => {
    if (!isRecommendsSupported()) {
      message.error("AI推荐功能仅支持PostChat项目");
      return;
    }

    if (!currentRecord?.id) return;

    setGeneratingRecommends(true);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/recommendations`,
        {
          summary_id: currentRecord.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("AI推荐生成任务已提交");
        await fetchRecommends(currentRecord.id);
      } else {
        message.error(response.data.msg || "生成AI推荐失败");
      }
    } catch (error) {
      console.error("生成AI推荐失败:", error);
      message.error("生成AI推荐失败，请重试");
    } finally {
      setGeneratingRecommends(false);
    }
  };

  // 获取播客配置
  const fetchPodcastConfig = React.useCallback(async () => {
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/podcasts/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setPodcastConfig(normalizePodcastConfig(response.data.data));
      }
    } catch (error) {
      console.error("获取播客配置失败:", error);
    }
  }, [API_BASE_URL, token]);

  // 获取播客白名单状态
  const fetchPodcastWhitelistStatus = React.useCallback(
    async (summaryId: number) => {
      if (!summaryId || !podcastConfig?.specified_mode) {
        setPodcastWhitelistStatus(null);
        return;
      }

      try {
        const response = await request.get(
          `${API_BASE_URL}/api/v3/podcasts/summary-switches`,
          {
            params: { project_id: currentRecord?.project_id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          setPodcastWhitelistStatus(
            getSummarySwitchStatus(response.data.data, summaryId)
          );
        } else {
          setPodcastWhitelistStatus(false);
        }
      } catch (error) {
        console.error("获取播客白名单状态失败:", error);
        setPodcastWhitelistStatus(false);
      }
    },
    [API_BASE_URL, currentRecord?.project_id, podcastConfig, token]
  );

  // 加入播客白名单
  const addToPodcastWhitelist = async (summaryId: number) => {
    try {
      const response = await request.patch(
        `${API_BASE_URL}/api/v3/podcasts/summary-switch`,
        {
          summary_id: summaryId,
          enabled: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        setPodcastWhitelistStatus(true);
        return true;
      } else {
        message.error(response.data.msg || "加入播客白名单失败");
        return false;
      }
    } catch (error: any) {
      console.error("加入播客白名单失败:", error);
      message.error(error.response?.data?.msg || "加入播客白名单失败");
      return false;
    }
  };

  const stopPodcastPolling = React.useCallback(() => {
    if (podcastPollingTimerRef.current !== null) {
      window.clearInterval(podcastPollingTimerRef.current);
      podcastPollingTimerRef.current = null;
    }
  }, []);

  const fetchPodcastDetail = React.useCallback(
    async (podcastId: number) => {
      const response = await request.get<{
        code: number;
        msg: string;
        data: PodcastInfo;
      }>(`${API_BASE_URL}/api/v3/podcasts/${podcastId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "获取播客详情失败");
      }

      return response.data.data;
    },
    [API_BASE_URL, token]
  );

  const startPodcastPolling = React.useCallback(
    (podcastId: number) => {
      stopPodcastPolling();

      const poll = async () => {
        try {
          const nextPodcast = await fetchPodcastDetail(podcastId);
          setPodcast(nextPodcast);
          setGeneratingProgress(getPodcastProgressText(nextPodcast));

          if (
            nextPodcast.status === PodcastStatus.Completed ||
            nextPodcast.status === PodcastStatus.Failed
          ) {
            stopPodcastPolling();
            setGeneratingPodcast(false);
            if (nextPodcast.status === PodcastStatus.Completed) {
              message.success("播客生成完成");
            }
          }
        } catch (error) {
          console.error("轮询播客状态失败:", error);
          stopPodcastPolling();
          setGeneratingPodcast(false);
          setGeneratingProgress("");
        }
      };

      void poll();
      podcastPollingTimerRef.current = window.setInterval(() => {
        void poll();
      }, 3000);
    },
    [fetchPodcastDetail, stopPodcastPolling]
  );

  // 获取摘要对应的播客
  const fetchPodcast = React.useCallback(
    async (summaryId: number) => {
      if (!summaryId) return;

      setPodcastLoading(true);
      try {
        const response = await request.get(`${API_BASE_URL}/api/v3/podcasts`, {
          params: {
            summary_id: summaryId,
            page: 1,
            page_size: 1,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code !== 200) {
          throw new Error(response.data.msg || "获取播客失败");
        }

        const list = Array.isArray(response.data.data?.list)
          ? response.data.data.list
          : [];
        const matchedPodcast = list.length > 0 ? list[0] : null;

        setPodcast(matchedPodcast);
        setGeneratingProgress(getPodcastProgressText(matchedPodcast));

        if (
          matchedPodcast &&
          (matchedPodcast.status === PodcastStatus.Pending ||
            matchedPodcast.status === PodcastStatus.Processing)
        ) {
          setGeneratingPodcast(true);
          startPodcastPolling(matchedPodcast.id);
        } else {
          setGeneratingPodcast(false);
        }
      } catch (error) {
        console.error("获取播客失败:", error);
        setPodcast(null);
        setGeneratingPodcast(false);
        setGeneratingProgress("");
      } finally {
        setPodcastLoading(false);
      }
    },
    [API_BASE_URL, token, startPodcastPolling]
  );

  // 生成播客（v3 创建 + 轮询详情）
  const handleGeneratePodcast = async () => {
    if (!currentRecord) return;

    // 如果开启了指定模式，且当前摘要不在白名单中，先加入白名单
    if (
      podcastConfig?.specified_mode &&
      podcastWhitelistStatus === false
    ) {
      const added = await addToPodcastWhitelist(currentRecord.id);
      if (!added) {
        return;
      }
      message.success("已加入播客白名单");
    }

    // 获取项目密钥
    const project = projects.find((p) => p.id === currentRecord.project_id);
    if (!project?.key_value) {
      message.error("无法获取项目密钥，请稍后重试");
      return;
    }

    setGeneratingPodcast(true);
    setGeneratingProgress("播客任务已提交，等待生成中...");

    try {
      const response = await request.post<{
        code: number;
        msg: string;
        data: PodcastInfo;
      }>(
        `${API_BASE_URL}/api/v3/podcasts`,
        {
          title: currentRecord.title,
          summary_id: currentRecord.id,
          speaker_type: podcastConfig?.speaker_type || "mizai",
          audio_format: podcastConfig?.audio_format || "mp3",
          use_head_music: podcastConfig?.use_head_music ?? true,
          use_tail_music: podcastConfig?.use_tail_music ?? false,
          speech_rate: podcastConfig?.speech_rate ?? 0,
          language: podcastConfig?.language || "zh-CN",
          action: 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200 && response.data.data?.id) {
        message.success("播客生成任务已提交");
        setPodcast(response.data.data);
        startPodcastPolling(response.data.data.id);
      } else {
        setGeneratingPodcast(false);
        setGeneratingProgress("");
        message.error(response.data.msg || "提交播客生成任务失败");
      }
    } catch (error: any) {
      console.error("提交播客生成任务失败:", error);
      setGeneratingPodcast(false);
      setGeneratingProgress("");
      message.error(error.response?.data?.msg || "提交播客生成任务失败");
    }
  };

  React.useEffect(() => {
    return () => {
      stopPodcastPolling();
    };
  }, [stopPodcastPolling]);

  // 播放播客
  const handlePlayPodcast = (podcast: PodcastInfo) => {
    setCurrentPodcast(podcast);
    setPlayerVisible(true);
  };

  // 获取热词列表
  const fetchHotWords = React.useCallback(
    async (summaryId: number) => {
      if (!summaryId) return;

      setHotWordsLoading(true);
      try {
        const response = await request.get(
          `${API_BASE_URL}/api/v3/hotwords`,
          {
            params: {
              summary_id: summaryId,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          const list = Array.isArray(response.data.data)
            ? response.data.data.filter(
                (item: HotWordItem) => item.summary_id === summaryId
              )
            : [];
          setHotWords(list);
          setHotWordsTotal(list.length);
        } else {
          message.error(response.data.msg || "获取热词列表失败");
        }
      } catch (error) {
        console.error("获取热词列表失败:", error);
        message.error("获取热词列表失败，请稍后重试");
      } finally {
        setHotWordsLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  // 初始化时获取播客配置
  useEffect(() => {
    if (visible) {
      fetchPodcastConfig();
    }
  }, [visible, fetchPodcastConfig]);

  useEffect(() => {
    if (currentRecord) {
      form.setFieldsValue({
        content: currentRecord.content || "",
        summary: currentRecord.summary || "",
      });
      setSaveSuccess(false);

      // 加载热词、AI推荐和播客
      if (currentRecord.id) {
        fetchHotWords(currentRecord.id);
        fetchRecommends(currentRecord.id);
        fetchPodcast(currentRecord.id);
        if (podcastConfig?.specified_mode) {
          fetchPodcastWhitelistStatus(currentRecord.id);
        }
      }
    } else {
      setPodcast(null);
      setGeneratingPodcast(false);
      setGeneratingProgress("");
      stopPodcastPolling();
    }
  }, [currentRecord, fetchHotWords, fetchRecommends, fetchPodcast, fetchPodcastWhitelistStatus, podcastConfig, form, stopPodcastPolling]);

  const handleGenerateHotWords = async () => {
    if (!isHotWordsSupported()) {
      message.error("热词功能仅支持PostChat项目");
      return;
    }

    if (!currentRecord?.id) return;

    setGeneratingHotWords(true);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/hotwords`,
        {
          summary_id: currentRecord.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("热词生成任务已提交");
        await fetchHotWords(currentRecord.id);
      } else {
        message.error(response.data.msg || "生成热词失败");
      }
    } catch (error) {
      console.error("生成热词失败:", error);
      message.error("生成热词失败，请重试");
    } finally {
      setGeneratingHotWords(false);
    }
  };

  // 检查项目是否支持AI索引（仅PostChat项目支持）
  const isAIIndexingSupported = () => {
    if (!currentRecord) return false;
    const project = projects.find((p) => p.id === currentRecord.project_id);
    return project?.is_postchat || false;
  };

  const handleRebuildIndex = async () => {
    if (!currentRecord?.id) return;

    if (!isAIIndexingSupported()) {
      message.error("AI索引功能仅支持PostChat项目");
      return;
    }

    setRebuildingIndex(true);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/knowledge/summaries/${currentRecord.id}/revector`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("索引重建成功");
        if (currentRecord) {
          currentRecord.is_vector = true;
        }
        onSuccess();
      } else {
        message.error(response.data.msg || "索引重建失败");
      }
    } catch (error: any) {
      console.error("索引重建失败:", error);
      message.error(
        "索引重建失败:" + (error.response?.data?.msg || "未知错误")
      );
    } finally {
      setRebuildingIndex(false);
    }
  };

  // 获取知识类型标签
  const getKnowledgeTypeTag = (record: KnowledgeItem | null) => {
    if (!record) return null;
    const type = record.doc_type || "text";

    switch (type) {
      case "file":
        return (
          <Tag icon={<UploadOutlined />} color="blue">
            文件
          </Tag>
        );
      case "html":
        return (
          <Tag icon={<GlobalOutlined />} color="green">
            网页
          </Tag>
        );
      default:
        return (
          <Tag icon={<FileTextOutlined />} color="purple">
            文本
          </Tag>
        );
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (!currentRecord) return;

      setLoading(true);
      try {
        const response = await request.put(
          `${API_BASE_URL}/api/v3/knowledge/summaries/${currentRecord.id}`,
          {
            content: values.content,
            summary: values.summary,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          setSaveSuccess(true);
          message.success("更新成功");
          onSuccess();
        } else {
          message.error(response.data.msg || "更新失败");
        }
      } catch (error) {
        message.error("更新失败，请重试");
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // 修改知识库详情卡片，添加重建索引按钮
  const renderInfoCard = () => {
    if (!currentRecord) return null;

    return (
      <Card
        size="small"
        bordered={false}
        style={{
          backgroundColor: "#f9f9f9",
          marginBottom: 16,
          borderRadius: "6px",
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Space>
              <BookOutlined />
              <Text strong>项目:</Text>
              <Tag color="blue">
                {getProjectName(currentRecord.project_id || 0)}
              </Tag>
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <Space>
              <GlobalOutlined />
              <Text strong>来源:</Text>
              {currentRecord.url ? (
                <a
                  href={currentRecord.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {new URL(currentRecord.url).hostname}
                </a>
              ) : (
                "-"
              )}
            </Space>
          </Col>
        </Row>
        <Divider style={{ margin: "8px 0" }} />
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Space>
              <CalendarOutlined />
              <Text strong>创建于:</Text>
              {new Date(currentRecord.created_at || "").toLocaleString("zh-CN")}
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <Space>
              <RobotOutlined />
              <Text strong>AI 索引状态:</Text>
              {isAIIndexingSupported() ? (
                <Space>
                  <Badge
                    status={currentRecord.is_vector ? "success" : "default"}
                    text={currentRecord.is_vector ? "已归档索引" : "未归档索引"}
                  />
                  <Button
                    type="link"
                    size="small"
                    icon={<SyncOutlined spin={rebuildingIndex} />}
                    onClick={handleRebuildIndex}
                    loading={rebuildingIndex}
                    disabled={rebuildingIndex}
                  >
                    重建索引
                  </Button>
                </Space>
              ) : (
                <Badge status="warning" text="当前项目不支持AI索引" />
              )}
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  // 热词表格列配置
  const hotWordColumns = [
    {
      title: "热词",
      dataIndex: "word",
      key: "word",
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "解释",
      dataIndex: "description",
      key: "description",
      render: (text: string) => text || "-",
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      render: (text: string) => new Date(text).toLocaleString("zh-CN"),
    },
  ];

  // 渲染AI推荐内容
  const renderRecommendContent = () => {
    return (
      <div style={{ marginBottom: 16 }}>
        {!isRecommendsSupported() ? (
          <Alert
            message="AI推荐功能仅限PostChat项目使用"
            description="当前知识所属项目为Token计费项目，不支持AI推荐功能。请升级项目为时长计费项目以使用此功能。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
              }}
            >
              <Alert
                message="AI推荐为HongMoAI根据历史内容自动生成的相关内容"
                type="info"
                showIcon
                style={{ marginBottom: 0, flex: 1 }}
              />
              <div style={{ marginLeft: 16 }}>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() =>
                      currentRecord?.id && fetchRecommends(currentRecord.id)
                    }
                    loading={recommendsLoading}
                  >
                    刷新
                  </Button>
                  {recommendsTotal === 0 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleGenerateRecommends}
                      loading={generatingRecommends}
                    >
                      生成推荐
                    </Button>
                  )}
                </Space>
              </div>
            </div>

            {recommendsTotal === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#999",
                }}
              >
                <BulbOutlined
                  style={{ fontSize: "48px", marginBottom: "16px" }}
                />
                <div>暂无AI推荐内容</div>
                <div style={{ fontSize: "12px", marginTop: "8px" }}>
                  系统会根据内容相似度自动生成推荐
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleGenerateRecommends}
                  loading={generatingRecommends}
                  style={{ marginTop: 16 }}
                >
                  立即生成
                </Button>
              </div>
            ) : (
              <List
                loading={recommendsLoading}
                dataSource={recommends}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        icon={<LinkOutlined />}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        访问
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          shape="square"
                          size="large"
                          icon={<BookOutlined />}
                          style={{ backgroundColor: "#1677ff" }}
                        />
                      }
                      title={
                        <Space>
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontWeight: "bold" }}
                          >
                            {item.title}
                          </a>
                          {item.score > 0 && (
                            <Tag color="green">
                              相似度: {item.score.toFixed(2)}
                            </Tag>
                          )}
                        </Space>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: "8px" }}>
                            {item.summary}
                          </div>
                          <div style={{ fontSize: "12px", color: "#999" }}>
                            <Space>
                              <BulbOutlined />
                              <span>{item.reason}</span>
                            </Space>
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <Modal
      getContainer={false}
      title={
        <Space>
          <EditOutlined style={{ color: "#1677ff" }} />
          <span>编辑知识</span>
          {currentRecord && getKnowledgeTypeTag(currentRecord)}
          <Text
            type="secondary"
            style={{ fontSize: "14px", fontWeight: "normal" }}
          >
            {currentRecord?.title}
          </Text>
        </Space>
      }
      open={visible}
      onOk={handleModalOk}
      onCancel={onCancel}
      okText={saveSuccess ? "已保存" : "保存"}
      okButtonProps={{
        icon: saveSuccess ? <CheckCircleOutlined /> : undefined,
        style: saveSuccess
          ? { background: "#52c41a", borderColor: "#52c41a" }
          : {},
      }}
      cancelText="取消"
      width={900}
      maskClosable={false}
      destroyOnClose
      confirmLoading={loading}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
          padding: "16px",
        },
        footer: {
          borderTop: "1px solid #f0f0f0",
          padding: "12px 24px",
        },
      }}
    >
      {currentRecord && (
        <>
          {/* 显示知识库详情卡片 */}
          {renderInfoCard()}

          {isAIIndexingSupported() ? (
            <Alert
              message={
                <Space>
                  <Text>
                    编辑知识内容后，系统会自动更新向量索引，以便于搜索和 AI 访问
                  </Text>
                  {currentRecord.is_vector && (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      已归档索引
                    </Tag>
                  )}
                </Space>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Alert
              message="当前知识所属项目不支持AI索引，仅PostChat项目支持此功能"
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          <Tabs activeKey={activeTab} onChange={handleTabChange} type="card">
            <TabPane
              tab={
                <span>
                  <EditOutlined /> 编辑内容
                </span>
              }
              key="content"
            >
              <Form
                form={form}
                layout="vertical"
                requiredMark="optional"
                initialValues={{
                  content: currentRecord?.content || "",
                  summary: currentRecord?.summary || "",
                }}
              >
                <Form.Item
                  name="content"
                  label={
                    <Space>
                      <FileTextOutlined />
                      <Text strong>知识内容</Text>
                      <Tooltip title="获取摘要时将根据此内容获取，如果为时长计费项目，此内容将作为 AI 索引的基础">
                        <InfoCircleOutlined style={{ color: "#1677ff" }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true, message: "请输入知识内容" }]}
                >
                  <TextArea
                    rows={12}
                    showCount
                    placeholder="请输入详细的知识内容，支持 Markdown 格式"
                    style={{
                      fontFamily: "monospace",
                      fontSize: "14px",
                      resize: "vertical",
                      minHeight: "120px",
                      maxHeight: "400px",
                      borderColor: "#d9d9d9",
                      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05) inset",
                      backgroundColor: "#fafafa",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="summary"
                  label={
                    <Space>
                      <BookOutlined />
                      <Text strong>摘要内容</Text>
                      <Tooltip title="简明扼要的内容概述，将作为知识库列表中的摘要展示">
                        <InfoCircleOutlined style={{ color: "#1677ff" }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <TextArea
                    rows={4}
                    showCount
                    maxLength={500}
                    placeholder="请输入简短的摘要内容，不超过500字（可选）"
                    style={{
                      borderColor: "#d9d9d9",
                      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05) inset",
                      backgroundColor: "#fafafa",
                    }}
                  />
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <InfoCircleOutlined /> 详细信息
                </span>
              }
              key="info"
            >
              <div style={{ padding: "8px 0" }}>
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  labelStyle={{ fontWeight: "bold", width: "120px" }}
                >
                  <Descriptions.Item
                    label={
                      <Space>
                        <BookOutlined />
                        <span>知识标题</span>
                      </Space>
                    }
                  >
                    <Text strong>{currentRecord.title}</Text>
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <BookOutlined />
                        <span>所属项目</span>
                      </Space>
                    }
                  >
                    <Tag color="blue">
                      {getProjectName(currentRecord.project_id || 0)}
                    </Tag>
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <GlobalOutlined />
                        <span>知识来源</span>
                      </Space>
                    }
                  >
                    {currentRecord.url ? (
                      <div style={{ wordBreak: "break-all" }}>
                        <a
                          href={currentRecord.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {currentRecord.url}
                        </a>
                      </div>
                    ) : (
                      "-"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <GlobalOutlined />
                        <span>所属网站</span>
                      </Space>
                    }
                  >
                    {currentRecord.refer ? (
                      <div style={{ wordBreak: "break-all" }}>
                        {currentRecord.refer}
                      </div>
                    ) : (
                      "-"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <CalendarOutlined />
                        <span>获取时间</span>
                      </Space>
                    }
                  >
                    {new Date(currentRecord.created_at || "").toLocaleString(
                      "zh-CN"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <CheckCircleOutlined />
                        <span>知识归档</span>
                      </Space>
                    }
                  >
                    {isAIIndexingSupported() ? (
                      <Space>
                        <Tag
                          color={
                            currentRecord.is_vector ? "success" : "default"
                          }
                        >
                          {currentRecord.is_vector ? "已归档" : "未归档"}
                        </Tag>
                        <Button
                          type="link"
                          size="small"
                          icon={<SyncOutlined spin={rebuildingIndex} />}
                          onClick={handleRebuildIndex}
                          loading={rebuildingIndex}
                          disabled={rebuildingIndex}
                        >
                          重建索引
                        </Button>
                      </Space>
                    ) : (
                      <Tag color="warning">不支持AI索引</Tag>
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <TagsOutlined />
                        <span>热词数量</span>
                      </Space>
                    }
                  >
                    {isHotWordsSupported() ? (
                      <Tag color="blue">{hotWordsTotal}</Tag>
                    ) : (
                      <Tooltip title="热词功能仅限PostChat项目使用">
                        <Tag color="default">不支持</Tag>
                      </Tooltip>
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={
                      <Space>
                        <BulbOutlined />
                        <span>AI推荐数量</span>
                      </Space>
                    }
                  >
                    {isRecommendsSupported() ? (
                      <Tag color="blue">{recommendsTotal}</Tag>
                    ) : (
                      <Tooltip title="AI推荐功能仅限PostChat项目使用">
                        <Tag color="default">不支持</Tag>
                      </Tooltip>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <TagsOutlined /> 热词管理{" "}
                  {hotWordsTotal > 0 && (
                    <Tag color="blue" style={{ marginLeft: 4 }}>{hotWordsTotal}</Tag>
                  )}
                </span>
              }
              key="hotwords"
            >
              <div style={{ marginBottom: 16 }}>
                {!isHotWordsSupported() ? (
                  // 非PostChat项目显示提示信息
                  <Alert
                    message="热词功能仅限PostChat项目使用"
                    description="当前知识所属项目为Token计费项目，不支持热词功能。请升级项目为时长计费项目以使用此功能。"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                      }}
                    >
                      <Alert
                        message="热词是您希望在文章中突出显示的关键术语，可以为用户提供更好的阅读体验和额外的解释信息，默认由系统自动生成"
                        type="info"
                        showIcon
                        style={{ marginBottom: 0, flex: 1 }}
                      />
                      {hotWordsTotal === 0 && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleGenerateHotWords}
                          loading={generatingHotWords}
                          style={{ marginLeft: 16 }}
                        >
                          生成热词
                        </Button>
                      )}
                    </div>

                    <Table
                      columns={hotWordColumns}
                      dataSource={hotWords}
                      rowKey="id"
                      loading={hotWordsLoading}
                      pagination={false}
                      locale={{ emptyText: "暂无热词数据" }}
                      size="small"
                      style={{ marginTop: 8 }}
                    />
                  </>
                )}
              </div>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <BulbOutlined /> AI推荐{" "}
                  {recommendsTotal > 0 && (
                    <Badge
                      count={recommendsTotal}
                      size="small"
                      offset={[5, -3]}
                    />
                  )}
                </span>
              }
              key="recommends"
            >
              {renderRecommendContent()}
            </TabPane>

            <TabPane
              tab={
                <span>
                  <i className="ri-mic-line" style={{ marginRight: 4 }} /> 播客
                </span>
              }
              key="podcast"
            >
              <div style={{ padding: "8px 0" }}>
                {podcastLoading ? (
                  <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin size="large" />
                  </div>
                ) : !podcast ? (
                  <Empty
                    description={
                      <Space direction="vertical" size="middle">
                        <Text type="secondary">此摘要尚未生成播客</Text>
                        {generatingProgress && (
                          <Alert
                            message={generatingProgress}
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                          />
                        )}
                        <Button
                          type="primary"
                          icon={<i className="ri-mic-line" />}
                          onClick={handleGeneratePodcast}
                          loading={generatingPodcast}
                          disabled={generatingPodcast}
                        >
                          {generatingPodcast ? "生成中..." : "立即生成"}
                        </Button>
                      </Space>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Card>
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                      <div>
                        <Space>
                          <Text strong style={{ fontSize: 16 }}>{podcast.title}</Text>
                          <Tag color={podcast.status === 2 ? "success" : podcast.status === 3 ? "error" : "processing"}>
                            {podcast.status_text}
                          </Tag>
                        </Space>
                      </div>
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Text type="secondary">
                          创建时间: {new Date(podcast.created_at).toLocaleString("zh-CN")}
                        </Text>
                        {podcast.completed_at && (
                          <Text type="secondary">
                            完成时间: {new Date(podcast.completed_at).toLocaleString("zh-CN")}
                          </Text>
                        )}
                        {podcast.duration && (
                          <Text type="secondary">
                            时长: {Math.floor(podcast.duration / 60)}分{Math.floor(podcast.duration % 60)}秒
                          </Text>
                        )}
                        {podcast.error_msg && (
                          <Text type="danger">错误: {podcast.error_msg}</Text>
                        )}
                      </Space>
                      {podcast.status === 2 && podcast.audio_url && (
                        <Button
                          type="primary"
                          icon={<i className="ri-play-circle-line" />}
                          onClick={() => handlePlayPodcast(podcast)}
                          size="large"
                          block
                        >
                          播放播客
                        </Button>
                      )}
                    </Space>
                  </Card>
                )}
              </div>
            </TabPane>
          </Tabs>
        </>
      )}

      <PodcastPlayer
        visible={playerVisible}
        onCancel={() => {
          setPlayerVisible(false);
          setCurrentPodcast(null);
        }}
        podcast={currentPodcast}
      />
    </Modal>
  );
};

export default KnowledgeEditModal;
